import { useState, FormEvent, ChangeEvent } from 'react';
import Button from '../shared/Button';
import Alert from '../shared/Alert';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../shared/FormInput';
import { TEXT } from '../shared/Typography';

const API_BASE = import.meta.env.VITE_API_BASE || '';

interface UserData {
  token: string;
  userId?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  roles?: string[];
  fullName?: string;
  _computedRoles?: string[];
}

interface SetInvitePasswordProps {
  inviteToken: string;
  onSuccess: (_userData: UserData) => void | Promise<void>;
  onCancel?: () => void;
}

export default function SetInvitePassword({
  inviteToken,
  onSuccess,
  onCancel,
}: SetInvitePasswordProps) {
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy) return;

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/set-invite-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: inviteToken, password, name }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.error || 'Failed to set password');
        setBusy(false);
        return;
      }

      const userData = { ...data.user, token: data.token };
      onSuccess(userData);
    } catch (err) {
      console.error('Set password error:', err);
      setError('An error occurred. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white shadow-md rounded-lg max-w-md w-full overflow-hidden">
        {/* Logo Section */}
        <div className="bg-blue-50 px-8 py-8">
          <div className="flex justify-center">
            <img
              src="/grautomates-dark.png"
              alt="GRAutomates"
              className="h-24 w-auto object-contain"
            />
          </div>
        </div>

        {/* Form Section */}
        <div className="px-8 py-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className={`mb-1 ${TEXT.titleLg}`}>Set Your Password</h2>
            <p className={TEXT.bodyMuted}>
              Create a password to activate your account
            </p>
          </div>

          {error && (
            <Alert variant="error" message={error} className="mb-4" />
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="invite-name" className={FIELD_LABEL_CLASS}>Full Name</label>
              <input
                id="invite-name"
                type="text"
                className={FIELD_INPUT_CLASS}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label htmlFor="invite-password" className={FIELD_LABEL_CLASS}>Password</label>
              <input
                id="invite-password"
                type="password"
                className={FIELD_INPUT_CLASS}
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
              />
            </div>

            <div>
              <label htmlFor="invite-confirm-password" className={FIELD_LABEL_CLASS}>Confirm Password</label>
              <input
                id="invite-confirm-password"
                type="password"
                className={FIELD_INPUT_CLASS}
                value={confirmPassword}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                minLength={6}
              />
            </div>

            <Button variant="primary" type="submit" disabled={busy} fullWidth>
              {busy ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Setting Password...
                </span>
              ) : (
                'Set Password & Continue'
              )}
            </Button>
          </form>

          {onCancel && (
            <Button variant="linkMuted" size="sm" onClick={onCancel} fullWidth className="mt-4">
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
